import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import {
  BaseAuthenticatedService,
  httpQueryParams,
  PaginatedResponse,
  RecursiveInterface,
} from './base-authenticated.service';
import { PreventiveInterface } from '@app/interface/preventive';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PreventiveApiService extends BaseAuthenticatedService {
  downloadInvoicePdf(invoiceId: number) {
    throw new Error('Method not implemented.');
  }
  constructor(
    public override auth: AngularFireAuth,
    public override httpClient: HttpClient,
    private route: ActivatedRoute, // Inject ActivatedRoute
  ) {
    super(auth, httpClient);
  }

  public getPreventiveList<T = PreventiveInterface>(
    organization_id: number | null,
    // projectId: number,
    params: httpQueryParams = {},
  ): Observable<PaginatedResponse<T>> {
    let path = `api/${organization_id}/preventive/`;
    return this.getList<T>(path, params);
  }

  // using getRecursivePage
  public getAllPreventives<T = PreventiveInterface>(
    organization_id: number | null,
    params: httpQueryParams = {},
  ): Subject<RecursiveInterface<T>> {
    const path = `api/${organization_id}/preventive/`;
    return this.getRecursivePage<T>(path, params);
  }

  public createPreventive(
    organization_id: number | null,
    preventiveData: Partial<PreventiveInterface>,
  ): Observable<PreventiveInterface> {
    const path = `api/${organization_id}/preventive/`;

    // TODO: date conversion (moment.js)

    return this.post<PreventiveInterface>(path, {}, preventiveData);
  }

  public updatePreventiveDetails(
    organization_id: number | null,
    id: number,
    data: Partial<PreventiveInterface>,
  ): Observable<PreventiveInterface> {
    console.log('data log.', data, 'id,', id);
    return this.partialUpdate<PreventiveInterface>(
      `api/${organization_id}/preventive/${id}/`,
      {}, //{ withCredentials: true },
      data,
    );
  }

  public softDeletePreventive(
    organization_id: number | null,
    id: number,
  ): Observable<PreventiveInterface> {
    const path = `api/${organization_id}/preventive/${id}/`;
    const deleteData = {
      is_deleted: true, // Assuming your Django model has a 'deleted' boolean field
      // deleted_at: new Date().toISOString() // Optional: if you track deletion timestamp
    };

    return this.update<PreventiveInterface>(path, {}, deleteData);
  }

  public getMaxProgressiveNumber(
    organization_id: number | null,
  ): Observable<number> {
    const path = `api/${organization_id}/preventive/max_preventive_progressive/`;
    return this.get<number>(path);
  }

  // generatepreventiveXml(
  //   id: number,
  //   data: Partial<PreventiveInterface>
  // ): Observable<Blob> {
  //   const headers = new HttpHeaders({
  //     Accept: 'application/xml',
  //   });

  //   let organization_id = 1;

  //   console.log(id);

  //   return this.http.get(`${this.apiUrl}/${id}/generate-xml/`, {
  //     headers: headers,
  //     responseType: 'blob', // Important for file downloads
  //   });
  // }

  // public duplicatepreventiveByData(
  //   organization_id: number | null,
  //   preventiveData: PreventiveInterface
  // ): Observable<PreventiveInterface> {
  //   // let organization_id = 1;
  //   const path = `api/${organization_id}/preventive/`;

  //   // Create a copy with modified fields to avoid conflicts
  //   const duplicateData = {
  //     ...preventiveData,
  //     id: undefined, // Remove the ID to create a new record
  //     preventive: `${preventiveData.name} (Copy)`,
  //     is_draft: true, // Typically duplicates start as drafts
  //     is_paid: false, // Reset payment status
  //     emission_date: new Date().toISOString().split('T')[0], // Current date
  //     created_at: undefined,
  //     updated_at: undefined,
  //   };

  //   return this.post<PreventiveInterface>(path, {}, duplicateData);
  // }

  private backendDomain = environment.backendDomain; // FIXME: proper backend domain from environment

  // The endpoints below are downloaded/previewed via plain fetch() (no Authorization
  // header, needed for direct blob/preview handling), so the backend requires a
  // short-lived signed token instead. We fetch it first (authenticated, like the rest
  // of the API) and append it as a query param on the actual download request.
  // Preventive shares the Invoice model/id space, so it uses the same invoice-scoped
  // download-token endpoint.
  private async getInvoiceDownloadToken(invoiceId: number): Promise<string> {
    const headers = await firstValueFrom(this.getAuthHeaders());
    const headersObj: Record<string, string> = {};
    headers.keys().forEach((key) => {
      const value = headers.get(key);
      if (value !== null) {
        headersObj[key] = value;
      }
    });

    const response = await fetch(
      `${this.backendDomain}/invoice/${invoiceId}/download-token/`,
      { headers: headersObj },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.token as string;
  }

  // Method 1: Using fetch API with proper blob handling
  async downloadPreventivePdf(
    preventiveId: number,
    style: string | 'style1',
  ): Promise<void> {
    try {
      // const response = await fetch(
      //   `${this.backendDomain}/invoice/${preventiveId}/preventive-pdf/`
      // );

      // Determine the endpoint based on the selected style
      let endpoint: string;

      // console.log('selected style', style);

      switch (style) {
        case 'style2':
          endpoint = `${this.backendDomain}/invoice/${preventiveId}/preventive-pdf2/`;
          break;
        case 'style3':
          endpoint = `${this.backendDomain}/invoice/${preventiveId}/preventive-pdf3/`;
          break;
        case 'style1':
        default:
          endpoint = `${this.backendDomain}/invoice/${preventiveId}/preventive-pdf/`;
          break;
      }

      const token = await this.getInvoiceDownloadToken(preventiveId);
      const response = await fetch(
        `${endpoint}?token=${encodeURIComponent(token)}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob with correct MIME type
      const blob = await response.blob();

      // Verify blob is actually a PDF
      if (blob.type !== 'application/pdf') {
        console.warn('Expected PDF but got:', blob.type);
      }

      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Preventivo.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }

  public async showPdfInDialog(
    preventiveId: number,
    style: string | 'style1',
  ): Promise<string> {
    try {
      let endpoint: string;

      switch (style) {
        case 'style2':
          endpoint = `${this.backendDomain}/invoice/${preventiveId}/preventive-pdf2/`;
          break;
        case 'style3':
          endpoint = `${this.backendDomain}/invoice/${preventiveId}/preventive-pdf3/`;
          break;
        case 'style1':
        default:
          endpoint = `${this.backendDomain}/invoice/${preventiveId}/preventive-pdf/`;
          break;
      }

      // Fix the URL path - added 'api' prefix to match your other endpoints
      // const response = await fetch(
      //   `${this.backendDomain}/invoice/${preventiveId}/pdf/`,
      // );
      const token = await this.getInvoiceDownloadToken(preventiveId);
      const response = await fetch(
        `${endpoint}?token=${encodeURIComponent(token)}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob
      const blob = await response.blob();

      // Verify it's a PDF
      if (blob.type !== 'application/pdf') {
        console.warn('Expected PDF but got:', blob.type);
      }

      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      return blobUrl;
    } catch (error) {
      console.error('Error loading PDF:', error);
      throw error;
    }
  }

  async downloadpreventiveXml(preventiveId: number): Promise<void> {
    try {
      const token = await this.getInvoiceDownloadToken(preventiveId);
      const response = await fetch(
        `${this.backendDomain}/invoice/${preventiveId}/xml/?token=${encodeURIComponent(token)}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob with correct MIME type
      const blob = await response.blob();

      // Verify blob is actually a PDF
      if (blob.type !== 'application/xml') {
        console.warn('Expected XML but got:', blob.type);
      }

      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `preventive_${preventiveId}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading XML:', error);
      throw error;
    }
  }

  public uploadpreventiveXml(
    organization_id: number | null,
    xmlFile: File,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('xml_file', xmlFile);

    const path = `api/${organization_id}/preventive/upload-xml/`;

    // return this.http.post(`${this.baseUrl}${path}`, formData, {
    //   reportProgress: true,
    //   observe: 'events'
    // });

    return this.post<PreventiveInterface>(path, {}, formData);
  }

  async sendEmailWithPDF(
    organization_id: number,
    invoice_id: number,
  ): Promise<void> {
    // const path = `invoice/${invoice_id}/send-email-to-company-with-pdf/`;

    // return this.post<InvoiceInterface>(path, {}, invoiceData);

    try {
      const token = await this.getInvoiceDownloadToken(invoice_id);
      const response = await fetch(
        `${this.backendDomain}/invoice/${invoice_id}/send-preventive-to-company-with-pdf/?token=${encodeURIComponent(token)}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }
}
